import { Fragment, useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import './UpdateBody.css'

/**
 * League-update body: URLs become links, image URLs and `![alt](url)` become
 * clickable thumbnails, `[name](/api/updates/files/…)` becomes a download,
 * and a line that starts with `-` becomes a bullet.
 *
 * @remarks
 * Plain text in, React nodes out — never `dangerouslySetInnerHTML`, so a
 * pasted URL cannot carry markup. Only `http`/`https` links, same-origin
 * uploaded images, and same-origin uploaded files are recognised.
 */

const URL_PATTERN = /https?:\/\/[^\s]+/gi
const TRAILING_PUNCT = /[.,;:!?]+$/
const HOSTED_IMAGE = /^\/api\/updates\/images\/[0-9a-f]{32}\.(png|jpe?g|gif|webp)$/i
const HOSTED_FILE = /^\/api\/updates\/files\/[0-9a-f]{32}\.[a-z0-9]{1,8}$/i
const IMAGE_PATH = /\.(png|jpe?g|gif|webp)$/i
const MD_IMAGE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+|\/api\/updates\/images\/[0-9a-f]{32}\.(?:png|jpe?g|gif|webp))\)/gi
const MD_LINK =
  /\[([^\]]+)\]\((\/api\/updates\/files\/[0-9a-f]{32}\.[a-z0-9]{1,8}|https?:\/\/[^\s)]+|\/api\/updates\/images\/[0-9a-f]{32}\.(?:png|jpe?g|gif|webp))\)/gi

function isBulletLine(line: string): boolean {
  return /^\s*-/.test(line)
}

function bulletText(line: string): string {
  return line.replace(/^\s*-\s*/, '')
}

function hrefAndDisplay(raw: string): { href: string; display: string; after: string } {
  let display = raw
  let after = ''
  const unmatchedClose = (display.match(/\)/g) ?? []).length > (display.match(/\(/g) ?? []).length
  if (unmatchedClose && display.endsWith(')')) {
    display = display.slice(0, -1)
    after = `)${after}`
  }
  const punct = TRAILING_PUNCT.exec(display)
  if (punct) {
    display = display.slice(0, -punct[0].length)
    after = `${punct[0]}${after}`
  }
  return { href: display, display, after }
}

/** Only http(s) image URLs and files we ourselves hosted. */
function safeImageSrc(href: string): string | null {
  if (HOSTED_IMAGE.test(href)) return href
  try {
    const url = new URL(href)
    if (url.protocol !== 'http:' && url.protocol !== 'https:') return null
    if (!IMAGE_PATH.test(url.pathname)) return null
    return url.href
  } catch {
    return null
  }
}

function hostedFileHref(href: string): string | null {
  return HOSTED_FILE.test(href) ? href : null
}

function soleImage(line: string): { src: string; alt: string } | null {
  const trimmed = line.trim()
  const markdown = /^!\[([^\]]*)\]\((.+)\)$/.exec(trimmed)
  if (markdown) {
    const src = safeImageSrc(markdown[2].trim())
    return src ? { src, alt: markdown[1] } : null
  }
  const { href } = hrefAndDisplay(trimmed)
  const src = safeImageSrc(href)
  return src && !/\s/.test(trimmed) ? { src, alt: '' } : null
}

function soleFile(line: string): { href: string; label: string } | null {
  const markdown = /^\[([^\]]+)\]\((.+)\)$/.exec(line.trim())
  if (!markdown) return null
  const href = hostedFileHref(markdown[2].trim())
  return href ? { href, label: markdown[1] } : null
}

function Thumbnail({
  src,
  alt,
  onOpen,
}: {
  src: string
  alt: string
  onOpen: (image: { src: string; alt: string }) => void
}) {
  const label = alt.trim() || 'View image'
  return (
    <button
      type="button"
      className="update-body__thumb"
      onClick={() => onOpen({ src, alt: label })}
      aria-label={label}
    >
      <img src={src} alt={alt} referrerPolicy="no-referrer" />
    </button>
  )
}

function FileChip({ href, label }: { href: string; label: string }) {
  return (
    <a className="update-body__file" href={href} download>
      Download {label}
    </a>
  )
}

function linkify(text: string, onOpen: (image: { src: string; alt: string }) => void): ReactNode {
  const nodes: ReactNode[] = []
  let key = 0
  const ranges: Array<{ start: number; end: number; node: ReactNode }> = []

  MD_IMAGE.lastIndex = 0
  let markdown = MD_IMAGE.exec(text)
  while (markdown) {
    const src = safeImageSrc(markdown[2])
    if (src) {
      ranges.push({
        start: markdown.index,
        end: markdown.index + markdown[0].length,
        node: <Thumbnail key={`md-${key}`} src={src} alt={markdown[1]} onOpen={onOpen} />,
      })
      key += 1
    }
    markdown = MD_IMAGE.exec(text)
  }

  MD_LINK.lastIndex = 0
  let link = MD_LINK.exec(text)
  while (link) {
    const startsInsideImage = ranges.some(
      range => link!.index >= range.start && link!.index < range.end
    )
    const isImageBang = link.index > 0 && text[link.index - 1] === '!'
    if (!startsInsideImage && !isImageBang) {
      const target = link[2]
      const file = hostedFileHref(target)
      const image = safeImageSrc(target)
      const node = file ? (
        <FileChip key={`file-${key}`} href={file} label={link[1]} />
      ) : image ? (
        <Thumbnail key={`mdlink-${key}`} src={image} alt={link[1]} onOpen={onOpen} />
      ) : (
        <a key={`md-${key}`} href={target} target="_blank" rel="noopener noreferrer">
          {link[1]}
        </a>
      )
      ranges.push({
        start: link.index,
        end: link.index + link[0].length,
        node,
      })
      key += 1
    }
    link = MD_LINK.exec(text)
  }

  ranges.sort((left, right) => left.start - right.start)

  function pushLinked(chunk: string): void {
    URL_PATTERN.lastIndex = 0
    let cursor = 0
    let match = URL_PATTERN.exec(chunk)
    while (match) {
      if (match.index > cursor) nodes.push(chunk.slice(cursor, match.index))
      const { href, display, after } = hrefAndDisplay(match[0])
      const image = safeImageSrc(href)
      if (image) {
        nodes.push(<Thumbnail key={`url-${key}`} src={image} alt="" onOpen={onOpen} />)
      } else {
        nodes.push(
          <a key={`a-${key}`} href={href} target="_blank" rel="noopener noreferrer">
            {display}
          </a>
        )
      }
      key += 1
      if (after) nodes.push(after)
      cursor = match.index + match[0].length
      match = URL_PATTERN.exec(chunk)
    }
    if (cursor < chunk.length) nodes.push(chunk.slice(cursor))
  }

  let last = 0
  for (const range of ranges) {
    if (range.start < last) continue
    if (range.start > last) pushLinked(text.slice(last, range.start))
    nodes.push(range.node)
    last = range.end
  }
  if (last < text.length) pushLinked(text.slice(last))
  return nodes
}

type Block =
  | { kind: 'list'; items: string[] }
  | { kind: 'paragraph'; lines: string[] }
  | { kind: 'images'; items: Array<{ src: string; alt: string }> }
  | { kind: 'files'; items: Array<{ href: string; label: string }> }

function parseBlocks(text: string): Block[] {
  const lines = text.replace(/\r\n/g, '\n').split('\n')
  const blocks: Block[] = []
  let index = 0

  while (index < lines.length) {
    const line = lines[index]
    if (line.trim() === '') {
      index += 1
      continue
    }

    if (isBulletLine(line)) {
      const items: string[] = []
      while (index < lines.length && isBulletLine(lines[index])) {
        items.push(bulletText(lines[index]))
        index += 1
      }
      blocks.push({ kind: 'list', items })
      continue
    }

    const firstImage = soleImage(line)
    if (firstImage) {
      const items = [firstImage]
      index += 1
      while (index < lines.length) {
        const next = soleImage(lines[index])
        if (!next) break
        items.push(next)
        index += 1
      }
      blocks.push({ kind: 'images', items })
      continue
    }

    const firstFile = soleFile(line)
    if (firstFile) {
      const items = [firstFile]
      index += 1
      while (index < lines.length) {
        const next = soleFile(lines[index])
        if (!next) break
        items.push(next)
        index += 1
      }
      blocks.push({ kind: 'files', items })
      continue
    }

    const paragraph: string[] = []
    while (
      index < lines.length &&
      lines[index].trim() !== '' &&
      !isBulletLine(lines[index]) &&
      !soleImage(lines[index]) &&
      !soleFile(lines[index])
    ) {
      paragraph.push(lines[index])
      index += 1
    }
    blocks.push({ kind: 'paragraph', lines: paragraph })
  }

  return blocks
}

function Lightbox({
  src,
  alt,
  onClose,
}: {
  src: string
  alt: string
  onClose: () => void
}) {
  useEffect(() => {
    function onKey(event: KeyboardEvent): void {
      if (event.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      document.body.style.overflow = previous
    }
  }, [onClose])

  return createPortal(
    <div
      className="update-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={alt}
      onClick={onClose}
    >
      <img src={src} alt={alt} referrerPolicy="no-referrer" />
    </div>,
    document.body
  )
}

export default function UpdateBody({ text }: { text: string }) {
  const [lightbox, setLightbox] = useState<{ src: string; alt: string } | null>(null)
  const blocks = parseBlocks(text)
  if (blocks.length === 0) return null

  return (
    <div className="update-body">
      {blocks.map((block, blockIndex) =>
        block.kind === 'list' ? (
          <ul key={blockIndex} className="update-body__list">
            {block.items.map((item, itemIndex) => (
              <li key={itemIndex}>{linkify(item, setLightbox)}</li>
            ))}
          </ul>
        ) : block.kind === 'images' ? (
          <div key={blockIndex} className="update-body__thumbs">
            {block.items.map((image, imageIndex) => (
              <Thumbnail
                key={imageIndex}
                src={image.src}
                alt={image.alt}
                onOpen={setLightbox}
              />
            ))}
          </div>
        ) : block.kind === 'files' ? (
          <div key={blockIndex} className="update-body__files">
            {block.items.map((file, fileIndex) => (
              <FileChip key={fileIndex} href={file.href} label={file.label} />
            ))}
          </div>
        ) : (
          <p key={blockIndex} className="update-body__p">
            {block.lines.map((line, lineIndex) => (
              <Fragment key={lineIndex}>
                {lineIndex > 0 && <br />}
                {linkify(line, setLightbox)}
              </Fragment>
            ))}
          </p>
        )
      )}
      {lightbox && <Lightbox src={lightbox.src} alt={lightbox.alt} onClose={() => setLightbox(null)} />}
    </div>
  )
}
